<template>
  <el-dialog
    title="选择仪器"
    :visible.sync="visible"
    width="1180px"
    top="4vh"
    append-to-body
    custom-class="pick-ins-picker-dlg"
    @open="onOpen"
  >
    <div class="pick-ins-picker" v-loading="loading">
      <div class="pick-ins-picker__toolbar">
        <el-radio-group v-model="deviceType" size="mini" @change="load">
          <el-radio-button label="field">现场仪器</el-radio-button>
          <el-radio-button label="lab">实验室</el-radio-button>
          <el-radio-button label="hygro">温湿度计</el-radio-button>
          <el-radio-button label="">全部</el-radio-button>
        </el-radio-group>
        <div class="pick-ins-picker__meta" v-if="total">共 {{ total }} 台</div>
        <div class="has-feedback pick-ins-picker__search">
          <input
            v-model="keyword"
            class="form-control"
            placeholder="名称 / 自编号 / 证书号 / 厂商"
            @keyup.enter="query"
          />
          <span class="fa fa-search form-control-feedback" aria-hidden="true" />
        </div>
      </div>

      <div class="pick-ins-picker__table-wrap">
        <table class="table table-bordered">
          <thead>
            <tr>
              <td style="width:40px;" />
              <td style="width:72px;">自编号</td>
              <td>仪器名称</td>
              <td style="width:110px;">型号</td>
              <td style="width:110px;">出厂编号</td>
              <td style="min-width:120px;">生产厂商</td>
              <td style="width:64px;">检定/校准</td>
              <td style="width:92px;">本次检定</td>
              <td style="width:92px;">有效期至</td>
              <td style="min-width:130px;">证书编号</td>
              <td style="min-width:120px;">检定单位</td>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filtered"
              :key="row.id"
              :class="{ 'is-selected': row.selected }"
              @click="row.selected = !row.selected"
            >
              <td @click.stop>
                <input type="checkbox" v-model="row.selected" />
              </td>
              <td>{{ row.code }}</td>
              <td>{{ row.name }}</td>
              <td>{{ row.model || '/' }}</td>
              <td>{{ row.factoryNo || '—' }}</td>
              <td :title="row.manufacturer">{{ row.manufacturer || '—' }}</td>
              <td>{{ row.traceType || '—' }}</td>
              <td>{{ row.dateLast || '—' }}</td>
              <td>{{ row.expiry || '—' }}</td>
              <td :title="row.certNo">{{ row.certNo || '—' }}</td>
              <td :title="row.certOrg">{{ row.certOrg || '—' }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="!filtered.length" class="text-center text-muted" style="padding:16px 0;">
          暂无仪器数据
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button type="primary" icon="el-icon-check" @click="confirm">确定</el-button>
      <el-button icon="el-icon-close" @click="visible = false">取消</el-button>
    </div>
  </el-dialog>
</template>

<script>
import api from '@/utils/api'

function cell(...vals) {
  for (const v of vals) {
    const s = String(v == null ? '' : v).trim()
    if (s) return s
  }
  return ''
}

function normalizeInstrument(row) {
  const name = cell(row.display_name, row.sb_name, row.cert_name, row.yqmc) || '未命名仪器'
  const model = cell(row.sb_model, row.cert_model, row.xh) || '/'
  const code = cell(row.sb_code, row.yqbh) || '—'
  return {
    id: row.id,
    name,
    model,
    code,
    factoryNo: cell(row.sb_factory_no, row.cert_factory_no),
    manufacturer: cell(row.sb_manufacturer, row.cert_manufacturer),
    traceType: cell(row.cert_trace_type),
    dateLast: cell(row.cert_date_last),
    expiry: cell(row.cert_expiry),
    certNo: cell(row.cert_no),
    certOrg: cell(row.cert_org),
    uncertainty: cell(row.cert_uncertainty),
    conclusion: cell(row.cert_conclusion),
    selected: false,
    raw: row
  }
}

export default {
  name: 'PickInstrumentPicker',
  props: {
    value: { type: Boolean, default: false },
    /** 已选仪器编号，分号分隔 */
    selectedCodes: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      keyword: '',
      /** 采样安排默认看现场台账 */
      deviceType: 'field',
      list: [],
      total: 0
    }
  },
  computed: {
    visible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) }
    },
    filtered() {
      const kw = String(this.keyword || '').trim().toLowerCase()
      if (!kw) return this.list
      return this.list.filter(r => {
        const hay = [
          r.name, r.model, r.code, r.factoryNo, r.manufacturer,
          r.certNo, r.certOrg, r.traceType
        ].join(' ').toLowerCase()
        return hay.indexOf(kw) >= 0
      })
    }
  },
  watch: {
    value(v) {
      if (v) {
        this.keyword = ''
        this.load()
      }
    }
  },
  methods: {
    onOpen() {
      this.keyword = ''
      this.deviceType = 'field'
      this.load()
    },
    query() { this.load() },
    async load() {
      this.loading = true
      try {
        const params = {
          page: 1,
          page_size: 500,
          keyword: String(this.keyword || '').trim()
        }
        if (this.deviceType) params.device_type = this.deviceType
        const res = await api.get('yn_tr_instruments_ledger', params)
        const data = res.data || {}
        const rows = Array.isArray(data)
          ? data
          : (data.rows || data.list || (data.data && (data.data.rows || data.data.list)) || [])
        this.total = Number(data.total || rows.length) || 0
        const selected = new Set(
          String(this.selectedCodes || '')
            .split(/[;；,，]/)
            .map(s => s.trim())
            .filter(Boolean)
        )
        this.list = rows.map(normalizeInstrument).map(r => ({
          ...r,
          selected: selected.has(String(r.id)) || selected.has(r.code) || selected.has(r.name)
        }))
      } catch (e) {
        this.list = []
        this.total = 0
        this.$message.error('加载仪器列表失败')
      } finally {
        this.loading = false
      }
    },
    confirm() {
      const picked = this.list.filter(r => r.selected)
      if (!picked.length) {
        this.$message.warning('请勾选仪器')
        return
      }
      this.$emit('pick', {
        ids: picked.map(r => r.id).join(';'),
        names: picked.map(r => r.name).join(';'),
        sizes: picked.map(r => r.model).join(';'),
        nos: picked.map(r => r.code).join(';'),
        objList: picked
      })
      this.visible = false
    }
  }
}
</script>

<style scoped>
.pick-ins-picker__toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.pick-ins-picker__meta {
  color: #909399;
  font-size: 12px;
  margin-right: auto;
}
.pick-ins-picker__search {
  position: relative;
  width: 240px;
  flex: 0 0 auto;
  margin-left: auto;
}
.pick-ins-picker__search .form-control {
  height: 30px;
  padding-right: 28px;
}
.pick-ins-picker__search .form-control-feedback {
  position: absolute;
  right: 8px;
  top: 7px;
  color: #999;
}
.pick-ins-picker__table-wrap {
  max-height: 60vh;
  overflow: auto;
}
.pick-ins-picker .table {
  margin-bottom: 0;
  font-size: 12px;
}
.pick-ins-picker .table thead td {
  background: #f5f7fa;
  font-weight: 600;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}
.pick-ins-picker .table td {
  vertical-align: middle;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pick-ins-picker tr.is-selected > td {
  background: #d9edf7;
}
</style>
